import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const mismatch = confirm && confirm !== pwd;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Link inválido. Solicite um novo em 'Esqueci minha senha'.");
      return;
    }
    if (pwd.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (pwd !== confirm) {
      toast.error("As senhas não coincidem. Digite a mesma senha nos dois campos.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Senha redefinida! Faça login com sua nova senha.");
      nav("/login", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao redefinir. O link pode ter expirado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="reset-back-to-login">
          <ArrowLeft size={16} /> Voltar ao login
        </Link>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
            <KeyRound size={22} className="text-primary" />
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Redefinir senha</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Escolha uma nova senha para sua conta no Rivendas.
          </p>

          {!token && (
            <div data-testid="reset-no-token" className="mt-6 bg-destructive/10 border border-destructive/30 rounded-xl p-4 text-sm">
              <p className="font-bold text-destructive">Link inválido ou expirado</p>
              <p className="text-muted-foreground mt-1">
                Solicite um novo link em <Link to="/esqueci-senha" className="text-primary underline">Esqueci minha senha</Link>.
              </p>
            </div>
          )}

          {token && (
            <form onSubmit={submit} noValidate className="space-y-4 mt-6">
              <div>
                <Label htmlFor="pwd">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="pwd"
                    data-testid="reset-password"
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    className="h-12 pr-11"
                  />
                  <button
                    type="button"
                    data-testid="reset-toggle-password"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm">Confirmar nova senha</Label>
                <Input
                  id="confirm"
                  data-testid="reset-confirm"
                  type={showPwd ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  placeholder="Digite a mesma senha"
                  className={`h-12 ${mismatch ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                {mismatch && (
                  <p data-testid="reset-mismatch" className="text-xs text-destructive mt-1">
                    As senhas não coincidem.
                  </p>
                )}
              </div>

              <Button
                type="submit"
                data-testid="reset-submit"
                disabled={loading || !pwd || !confirm || mismatch}
                className="w-full h-12 text-base font-bold"
              >
                {loading ? "Salvando..." : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
